import {
  RevAvatar,
  RevButton,
  RevIconButton,
  RevLink,
  RevStatusBadge,
  RevTooltip,
} from "@revmatics/catalyst";
import { getPlatformIcon } from "../../../../../../../../helpers/functions/getPlatformIcon.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fullFormatNumbers } from "../../../../../../../../helpers/functions/formatNumber.ts";
import { getScoreTextColor } from "../../data.ts";
import { CreatorData } from "../../../../../../../../stores/types/creatorsTypes.tsx";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";

import { HideCreatorMenu } from "./HideCreatorMenu.tsx";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getFitNameByNumber } from "../../../../../../../../helpers/functions/getFitName.ts";
import { LikeButton } from "./LikeButton.tsx";

type Props = {
  data: CreatorData;
  handleSelect?: () => void;
  selected?: boolean;
  maxSelectedCreators?: boolean;
  onboardingOpenDetails?: () => void;
};

export const CreatorCard = ({
  data,
  handleSelect,
  selected,
  maxSelectedCreators,
  onboardingOpenDetails,
}: Props) => {
  const { hideCreator: ffHideCreator, likeCreator: ffLikeCreator } = useFlags();
  return (
    <div
      onClick={selected ? undefined : handleSelect}
      className={`p-6 border border-monochrome-200 rounded-lg flex flex-col gap-3 h-full relative 
      ${!selected && handleSelect && !maxSelectedCreators ? "cursor-pointer transition-all ring-2 ring-transparent hover:ring-primary-500" : ""}
      ${handleSelect ? "px-6 pt-6 pb-14 bg-white" : "p-6"}`}
    >
      <div className="flex justify-between items-center gap-2">
        <div className="flex gap-2 items-center w-3/4">
          <RevAvatar
            variant="circle"
            size="medium"
            theme="light"
            image={data.creator_image_link || data.creator_image_file || ""}
            firstName={data.name.split(" ")[0]}
            lastName={data.name.split(" ").pop()}
          />
          <div className="w-[calc(100%_-_56px)]">
            {handleSelect ? (
              <h6 className="whitespace-nowrap overflow-hidden overflow-ellipsis">
                {data.name}
              </h6>
            ) : (
              <RevLink
                pathname=""
                search={`creatorId=${data.id}${handleSelect ? "" : "&menu=true"}`}
                text={data.name}
                className="w-fit !text-md !font-bold"
                underline={false}
                data-testid="creator-card-name-heading"
              />
            )}
            {data.platforms && data.platforms.length > 0 && (
              <div className="flex gap-1 items-center">
                {data.platforms.slice(0, 4).map(({ platform }) => {
                  const icon = getPlatformIcon(platform.name);
                  return icon ? (
                    <RevTooltip
                      key={`creator-card-platform-${platform.name}`}
                      content={platform.name}
                      position="top"
                    >
                      <div className="w-6 h-6 min-w-6 min-h-6 border rounded-lg border-monochrome-400 flex justify-center items-center transition-colors hover:border-primary-500 group">
                        <FontAwesomeIcon
                          icon={icon}
                          className="text-monochrome-700 text-md transition-colors group-hover:text-primary-500"
                        />
                      </div>
                    </RevTooltip>
                  ) : (
                    <span
                      className="typography-body-xs text-monochrome-700"
                      key={`creator-card-platform-${platform.name}`}
                    >
                      {platform.name}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {data.ai_score !== null && (
          <div>
            <p className="typography-body-xs text-monochrome-600 whitespace-nowrap">
              AI Score
            </p>
            <h4
              className={`${getScoreTextColor(Math.round(data.ai_score))} text-end`}
            >
              {Math.round(data.ai_score)}
            </h4>
          </div>
        )}
      </div>
      {data.ai_score !== null && (
        <div className="flex gap-1 items-center">
          <p className="typography-body-xs text-monochrome-600">Fit:</p>
          <p className="typography-body-sm text-monochrome-900">
            {getFitNameByNumber(data.ai_score)}
          </p>
        </div>
      )}
      {data.interests && data.interests.length > 0 && (
        <div className="flex gap-2 items-center flex-wrap">
          {data.interests.slice(0, 3).map((interest) => (
            <RevStatusBadge
              text={interest}
              key={`creator-card-interest-${interest}`}
              textClassName="whitespace-nowrap overflow-hidden overflow-ellipsis"
              className="max-w-[240px] overflow-hidden"
            />
          ))}
        </div>
      )}
      <div className="flex gap-4 items-center flex-wrap">
        {data.total_followers !== null && (
          <div>
            <p className="typography-body-xs text-monochrome-600">
              Total Followers
            </p>
            <p className="typography-body-bold-sm text-monochrome-900">
              {fullFormatNumbers({
                value: data.total_followers,
                shortenValue: 1e3,
                decimalCount: data.total_followers > 1e3 ? 1 : 0,
              })}
            </p>
          </div>
        )}
        {data.engagement !== null && (
          <div>
            <p className="typography-body-xs text-monochrome-600">Engagement</p>
            <p className="typography-body-bold-sm text-monochrome-900">
              {`${(data.engagement * 100).toFixed(2)}%`}
            </p>
          </div>
        )}
        {data.estimated_reach !== null && (
          <div>
            <p className="typography-body-xs text-monochrome-600">
              Estimated reach
            </p>
            <p className="typography-body-bold-sm text-monochrome-900">
              {fullFormatNumbers({
                value: data.estimated_reach,
                shortenValue: 1e3,
                decimalCount: data.estimated_reach > 1e3 ? 1 : 0,
              })}
            </p>
          </div>
        )}
      </div>
      {handleSelect && !onboardingOpenDetails && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-6 left-6"
        >
          <RevLink
            pathname=""
            search={`creatorId=${data.id}`}
            text="View Details"
            linkFontSize="extraSmall"
            underline={false}
          />
        </div>
      )}
      {handleSelect && onboardingOpenDetails && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-6 left-6"
        >
          <RevButton
            displayAs="link"
            linkFontSize="extraSmall"
            underline={false}
            text="View Details"
            handleClick={onboardingOpenDetails}
          />
        </div>
      )}
      {!handleSelect && (
        <div className="flex items-center gap-3">
          {ffLikeCreator && (
            <LikeButton
              creatorId={data.id}
              initLiked={data.user_interaction_status === "liked"}
            />
          )}
          {ffHideCreator && (
            <HideCreatorMenu
              creatorId={data.id}
              id="card"
              initHidden={data.user_interaction_status === "hidden"}
              width="w-fit"
            />
          )}
        </div>
      )}
      {selected && (
        <RevIconButton
          className="absolute bottom-6 right-6"
          icon={faTrash}
          iconSize="small"
          handleClick={handleSelect}
        />
      )}
    </div>
  );
};
